import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const CATEGORIES = [
  { value: 'support',  label: '🙋 Support',        color: '#007AFF' },
  { value: 'bug',      label: '🐛 Bug Report',      color: '#FF3B30' },
  { value: 'feature',  label: '✨ Feature Request', color: '#00C48C' },
  { value: 'feedback', label: '💬 Feedback',        color: '#FF9500' },
];

export default function SupportChat() {
  const { user } = useAuth();
  const [open, setOpen]         = useState(false);
  const [category, setCategory] = useState('support');
  const [message, setMessage]   = useState('');
  const [sent, setSent]         = useState(false);
  const [reply, setReply]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const textRef = useRef(null);

  useEffect(() => {
    if (open && textRef.current) textRef.current.focus();
  }, [open]);

  if (!user) return null;

  async function handleSubmit() {
    if (!message.trim()) return;
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('tbolt_token');
      const res = await fetch(`${API}/payments/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category, message: message.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setReply(data.reply);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setMessage(''); setSent(false); setReply(''); setError(''); setCategory('support');
  }

  const cat = CATEGORIES.find(c => c.value === category);

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(o => !o)} style={s.fab} aria-label="Support">
        {open
          ? <svg width="20" height="20" viewBox="0 0 20 20" fill="white"><path d="M4 4l12 12M16 4L4 16" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        }
      </button>

      {/* Panel */}
      {open && (
        <div style={s.panel}>
          <div style={s.header}>
            <div>
              <div style={s.headerTitle}>Thunderbolt Support</div>
              <div style={s.headerSub}>We typically respond within 24 hours</div>
            </div>
            <button onClick={() => setOpen(false)} style={s.closeBtn}>✕</button>
          </div>

          <div style={s.body}>
            {!sent ? (
              <>
                <div style={s.catRow}>
                  {CATEGORIES.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setCategory(c.value)}
                      style={{
                        ...s.catPill,
                        background: category === c.value ? c.color : '#F5F5F7',
                        color:      category === c.value ? '#fff'   : '#1D1D1F',
                        border:     `1.5px solid ${category === c.value ? c.color : '#E5E5EA'}`,
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <textarea
                  ref={textRef}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={
                    category === 'bug'     ? 'Describe what happened and steps to reproduce...' :
                    category === 'feature' ? "Describe the feature you'd like to see..." :
                    category === 'support' ? 'What do you need help with?' :
                                            'Share your thoughts with us...'
                  }
                  style={s.textarea}
                  rows={5}
                />

                {error && <div style={s.error}>{error}</div>}

                <button
                  onClick={handleSubmit}
                  disabled={loading || !message.trim()}
                  style={{ ...s.sendBtn, background: loading || !message.trim() ? '#C7C7CC' : cat.color }}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </>
            ) : (
              <div style={s.successWrap}>
                <div style={s.successIcon}>✓</div>
                <div style={s.successTitle}>Message received</div>
                <div style={s.successReply}>{reply}</div>
                <button onClick={handleReset} style={s.newMsgBtn}>Send another message</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  fab:          { position:'fixed', bottom:80, right:20, zIndex:1000, width:52, height:52, borderRadius:'50%', background:'#007AFF', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 20px rgba(0,122,255,0.4)' },
  panel:        { position:'fixed', bottom:144, right:20, zIndex:999, width:340, background:'#fff', borderRadius:20, boxShadow:'0 8px 40px rgba(0,0,0,0.15)', overflow:'hidden' },
  header:       { background:'#007AFF', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'flex-start' },
  headerTitle:  { color:'#fff', fontWeight:700, fontSize:15 },
  headerSub:    { color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:2 },
  closeBtn:     { background:'none', border:'none', color:'#fff', fontSize:16, cursor:'pointer', padding:0, lineHeight:1 },
  body:         { padding:20 },
  catRow:       { display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 },
  catPill:      { fontSize:12, fontWeight:500, padding:'5px 10px', borderRadius:20, cursor:'pointer', transition:'all 0.15s' },
  textarea:     { width:'100%', padding:'12px 14px', fontSize:14, lineHeight:1.5, border:'1.5px solid #E5E5EA', borderRadius:12, resize:'vertical', fontFamily:'inherit', color:'#1D1D1F', outline:'none', background:'#FAFAFA', boxSizing:'border-box', minHeight:100 },
  error:        { fontSize:13, color:'#FF3B30', marginTop:8, marginBottom:4 },
  sendBtn:      { width:'100%', padding:13, border:'none', borderRadius:12, color:'#fff', fontWeight:600, fontSize:15, cursor:'pointer', marginTop:12, transition:'background 0.15s' },
  successWrap:  { textAlign:'center', padding:'20px 0' },
  successIcon:  { width:48, height:48, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', fontSize:22, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' },
  successTitle: { fontWeight:700, fontSize:16, color:'#1D1D1F', marginBottom:8 },
  successReply: { fontSize:14, color:'#6E6E73', lineHeight:1.5, marginBottom:20 },
  newMsgBtn:    { background:'none', border:'1.5px solid #007AFF', color:'#007AFF', borderRadius:10, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer' },
};
